import React, { FC, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styles from "./index.module.css";
import clipboard from "../../assets/clipboard.svg";
import Dropper from "../../components/Dropper/Dropper";
import IkhokhaLoader from "../../assets/iK-Load-Animation.gif";
import { getInvoices, getMerchantDetails } from "../../api/statement-service/requests";
import { calculateTotalFromProductList } from "../../utils/Calculation";
import centsToRand, { getDefaultInvoiceNumber, numberWithSpaces, setNavigateOptions } from "../../utils/utils";
import { setMerchantDetails } from "../../redux/reducer/authentication";
import { isHttpErrorResponse } from "../../api/util";
import { BaseStatement } from "../../api/statement-service/types";
import { setSessionStorageData, StorageKeys } from "../../utils/Storage";
import {
  authErrorHeader,
  authErrorMessage,
  invoiceErrorHeader,
  invoiceErrorMessage,
  merchantErrorHeader,
  merchantErrorMessage,
} from "../../utils/ErrorMessages";
import caret from "../../assets/caret.svg";
import { StatementStatus } from "../../types";

const formatDate = (date: string): string => {
  const currentTimeZoneDate = new Date(date);
  return currentTimeZoneDate.toLocaleDateString();
};

// desktop history
interface DesktopHistoryProps {
  showLoader: boolean;
  invoices: React.MutableRefObject<any[]>;
  firstInvoice: React.MutableRefObject<string>;
  lastInvoice: React.MutableRefObject<string>;
  getNextInvoices: (_lastInvoice: React.MutableRefObject<string>) => void;
  getPreviousInvoices: (_lastInvoice: React.MutableRefObject<string>) => void;
}
const DesktopHistory: FC<DesktopHistoryProps> = ({
  showLoader,
  invoices,
  firstInvoice,
  lastInvoice,
  getNextInvoices,
  getPreviousInvoices,
}) => {
  // TODO: move into utils
  /**
   * This function takes a name and returns the first letter
   * of all the words in the name with the last name appended as it is
   * @example "Muhammad Mubeen Hamid" => "M M Hamid"
   * @param name
   */
  const formatCustomerName = (name: string): string => {
    let displayName: string = "";
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < name.length; i++) {
      if (i === name.length - 1) {
        displayName += name[i];
        break;
      }
      displayName = `${displayName}${name[i].charAt(0)} `;
    }
    return displayName;
  };

  return (
    <div className={styles.DesktopMain}>
      <div className={styles.Card}>
        {showLoader ? (
          <div>
            <img src={IkhokhaLoader} alt="loader" />
          </div>
        ) : (
          <div className={styles.HistoryContainer}>
            <div className={styles.Header}>
              <span>Invoice History</span>
              <span className={styles.PaginationButtons}>
                <span
                  onClick={() => getPreviousInvoices(firstInvoice)}
                  onKeyDown={() => getPreviousInvoices(firstInvoice)}
                  role="button"
                  tabIndex={0}
                >
                  <img
                    src={caret}
                    alt="left"
                    className={`${styles.leftCaret} ${
                      firstInvoice.current.length > 0 ? `${styles.Previous}` : `${styles.Disabled}`
                    }`}
                  />
                </span>
                <span
                  onClick={() => getNextInvoices(lastInvoice)}
                  onKeyDown={() => getNextInvoices(lastInvoice)}
                  role="button"
                  tabIndex={0}
                >
                  <img
                    src={caret}
                    alt="right"
                    className={`${styles.rightCaret} ${lastInvoice.current.length > 0 ? `${styles.Next}` : `${styles.Disabled}`}`}
                  />
                </span>
              </span>
            </div>
            <div className={styles.History}>
              <div className={styles.Header}>
                <div className={styles.Cell}>Invoice No.</div>
                <div className={styles.StatusCell}>Status</div>
                <div className={`${styles.Cell} ${styles.Customer}`}>Customer</div>
                <div className={styles.Cell}>Date Created</div>
                <div className={`${styles.Cell} ${styles.Due}`}>Due Date</div>
                <div className={styles.Cell}>Total</div>
                <div className={styles.Spacer} />
              </div>
              <div className={styles.HistoryContent}>
                {invoices.current.map((invoice: any) => {
                  const status = invoice.status.charAt(0).toUpperCase() + invoice.status.substring(1).toLowerCase();
                  const name = invoice.customer.customerName.split(" ");
                  const displayName = formatCustomerName(name);
                  const isInvoicePaid = invoice.status.toLowerCase() === StatementStatus.PAID.toLowerCase();
                  return (
                    <div className={styles.Row} key={invoice.id}>
                      <div className={styles.Cell}>{invoice.invoiceReference}</div>
                      <div className={`${styles.StatusCell} ${isInvoicePaid ? styles.PaidStatusCell : styles.UnpaidStatusCell}`}>
                        {status.toUpperCase()}
                      </div>
                      <div className={`${styles.Cell} ${styles.Customer}`}>{displayName}</div>
                      <div className={styles.Cell}>{formatDate(invoice.createDate)}</div>
                      <div className={`${styles.Cell} ${styles.Due}`}>{invoice.dueDate ? formatDate(invoice.dueDate) : "-"}</div>
                      <div className={styles.Cell}>
                        R {numberWithSpaces(centsToRand(parseFloat(calculateTotalFromProductList(invoice.productList))))}
                      </div>
                      <Dropper invoiceCreateDate={invoice.createDate} invoiceId={invoice.id} />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// mobile history
interface MobileHistoryProps {
  showLoader: boolean;
  invoices: React.MutableRefObject<any[]>;
  firstInvoiceSK: React.MutableRefObject<string>;
  lastInvoiceSK: React.MutableRefObject<string>;
  getNextInvoices: (_lastInvoice: React.MutableRefObject<string>) => void;
  getPreviousInvoices: (_lastInvoice: React.MutableRefObject<string>) => void;
}
const MobileHistory: FC<MobileHistoryProps> = ({
  showLoader,
  invoices,
  firstInvoiceSK,
  lastInvoiceSK,
  getNextInvoices,
  getPreviousInvoices,
}) => {
  return (
    <div className={styles.MobileMain}>
      <div className={styles.MobileMainContent}>
        <div className={styles.MobileHeader}>
          <span>Invoice History</span>
          <div className={styles.Pagination}>
            <span
              onClick={() => getPreviousInvoices(firstInvoiceSK)}
              onKeyDown={() => getPreviousInvoices(firstInvoiceSK)}
              role="button"
              tabIndex={0}
            >
              <img
                src={caret}
                alt="left"
                className={`${styles.leftCaret} ${
                  firstInvoiceSK.current.length > 0 ? `${styles.Previous}` : `${styles.Disabled}`
                }`}
              />
            </span>
            <span
              onClick={() => getNextInvoices(lastInvoiceSK)}
              onKeyDown={() => getNextInvoices(lastInvoiceSK)}
              role="button"
              tabIndex={0}
            >
              <img
                src={caret}
                alt="right"
                className={`${styles.rightCaret} ${lastInvoiceSK.current.length > 0 ? `${styles.Next}` : `${styles.Disabled}`}`}
              />
            </span>
          </div>
        </div>
        {showLoader ? (
          <div className={styles.Loader}>
            <img src={IkhokhaLoader} alt="loader" />
          </div>
        ) : (
          <div className={styles.MobileHistory}>
            {invoices.current.map((invoice: any) => {
              const status = invoice.status.charAt(0).toUpperCase() + invoice.status.substring(1).toLowerCase();
              const isInvoicePaid = invoice.status.toLowerCase() === StatementStatus.PAID.toLowerCase();
              return (
                <div className={styles.MobilePanel} key={invoice.id}>
                  <div className={styles.MobilePanelRowOne}>
                    <div className={styles.MobilePanelRowOneColOne}>{invoice.invoiceReference}</div>
                    <div className={styles.MobilePanelRowOneColTwo}>
                      <Dropper invoiceCreateDate={invoice.createDate} invoiceId={invoice.id} />
                    </div>
                  </div>
                  <div className={styles.MobilePanelRowTwo}>
                    <div className={styles.MobilePanelRowTwoColOne}>
                      <div className={styles.MobilePanelRowTwoColOneRowOne}>{invoice.customer.customerName}</div>
                      <div className={styles.MobilePanelRowTwoColOneRowTwo}>Created: {formatDate(invoice.createDate)}</div>
                    </div>
                    <div
                      className={`${styles.MobilePanelRowTwoColTwo} ${
                        isInvoicePaid ? styles.PaidStatusCell : styles.UnpaidStatusCell
                      }`}
                    >
                      {status.toUpperCase()}
                    </div>
                  </div>
                  <div className={styles.MobilePanelRowThree}>
                    <div className={styles.MobilePanelRowThreeColOne}>
                      Due: {invoice.dueDate ? formatDate(invoice.dueDate) : "-"}
                    </div>
                    <div className={styles.MobilePanelRowThreeColTwo}>
                      R {numberWithSpaces(centsToRand(parseFloat(calculateTotalFromProductList(invoice.productList))))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

interface LandingViewProps {
  showLoader: boolean;
}
const LandingView: FC<LandingViewProps> = ({ showLoader }) => (
  <div className={styles.Main}>
    <div className={styles.Card}>
      {showLoader ? (
        <div className="d-flex direction-col justify-content-center align-items-center">
          <img src={IkhokhaLoader} alt="loader" />
          <p className="text-size-16">Loading your Invoice History</p>
        </div>
      ) : (
        <>
          <img src={clipboard} alt="clipboard" />
          <ol>
            <li>Create an iK Invoice.</li>
            <li>
              Add customer and product details.
              <span className="text-bold">Please note that the invoice limit amount is R80 000 per invoice.</span>
            </li>
            <li>Share the invoice with your customer via email or WhatsApp</li>
            <li>
              Your customer clicks <q>Pay</q> button and enters their card details to pay you.
            </li>
          </ol>
        </>
      )}
    </div>
  </div>
);

const History = () => {
  const navigate = useNavigate();
  const [showLoader, setShowLoader] = useState(true);
  const invoices = useRef<BaseStatement[]>([]);
  const firstInvoice = useRef("");
  const lastInvoice = useRef("");
  const loggedInMerchant = `${useSelector(
    // @ts-ignore
    state => state.authenticationReducer.loggedInMerchant,
  )}`;
  const dispatch = useDispatch();

  // ================> useEffect <=================
  useEffect(() => {
    getMerchantDetails(loggedInMerchant).then(merchant => {
      if (isHttpErrorResponse(merchant)) {
        if (merchant.statusCode === 401) {
          navigate(`/error`, {
            state: setNavigateOptions(false, "", authErrorHeader, authErrorMessage),
          });
        } else {
          navigate(`/error`, {
            state: setNavigateOptions(true, "/", merchantErrorHeader, merchantErrorMessage),
          });
        }
        return;
      }
      const { ownerInfo } = merchant;
      dispatch(setMerchantDetails(ownerInfo));
      setSessionStorageData(StorageKeys.INVOICE_REFERENCE, getDefaultInvoiceNumber(ownerInfo.companyName));
      getInvoices(loggedInMerchant, 10, false, "").then(data => {
        if (isHttpErrorResponse(data)) {
          navigate(`/error`, {
            state: setNavigateOptions(true, "/", invoiceErrorHeader, invoiceErrorMessage),
          });
          return;
        }

        if (data.statements.length <= 0) {
          setShowLoader(false);
          return;
        }

        invoices.current = data.statements;
        firstInvoice.current = "";
        lastInvoice.current =
          data.lastEvaluatedKey === undefined
            ? ""
            : `${data.lastEvaluatedKey.sk.split("#")[1]}%23${data.lastEvaluatedKey.sk.split("#")[2]}`;
        setShowLoader(false);
      });
    });
  }, []);

  // ================> Get Next Invoices <=================
  const getNextInvoices = (lastInvoice: React.MutableRefObject<string>): void => {
    setShowLoader(true);
    if (!lastInvoice.current) {
      setShowLoader(false);
      return;
    }
    getInvoices(loggedInMerchant, 10, false, lastInvoice.current)
      .then(data => {
        if (isHttpErrorResponse(data)) {
          navigate(`/error`, {
            state: setNavigateOptions(true, "/", invoiceErrorHeader, invoiceErrorMessage),
          });
          return;
        }
        // eslint-disable-next-line no-param-reassign
        lastInvoice.current =
          data.lastEvaluatedKey === undefined
            ? ""
            : `${data.lastEvaluatedKey.sk.split("#")[1]}%23${data.lastEvaluatedKey.sk.split("#")[2]}`;

        invoices.current = data.statements;

        if (data.statements.length >= 0) {
          firstInvoice.current = `${data.statements[0].createDate}%23${data.statements[0].id}`;
        } else {
          firstInvoice.current = "";
        }

        setShowLoader(false);
      })
      .catch(() => {
        navigate(`/error`, {
          state: setNavigateOptions(true, "/", invoiceErrorHeader, invoiceErrorMessage),
        });
      });
  };

  // ================> Get Previous Invoices <=================
  const getPreviousInvoices = (firstInvoice: React.MutableRefObject<string>) => {
    setShowLoader(true);
    if (!firstInvoice.current) {
      setShowLoader(false);
      return;
    }
    getInvoices(loggedInMerchant, 10, true, firstInvoice.current)
      .then(data => {
        if (isHttpErrorResponse(data)) {
          navigate(`/error`, {
            state: setNavigateOptions(true, "/", invoiceErrorHeader, invoiceErrorMessage),
          });
          return;
        }
        // eslint-disable-next-line no-param-reassign
        firstInvoice.current =
          data.lastEvaluatedKey === undefined
            ? ""
            : `${data.lastEvaluatedKey.sk.split("#")[1]}%23${data.lastEvaluatedKey.sk.split("#")[2]}`;

        invoices.current = data.statements;

        if (data.statements.length > 0) {
          // eslint-disable-next-line no-param-reassign
          const lastItem = data.statements[data.statements.length - 1];
          lastInvoice.current = `${lastItem.createDate}%23${lastItem.id}`;
        } else {
          // eslint-disable-next-line no-param-reassign
          lastInvoice.current = "";
        }

        setShowLoader(false);
      })
      .catch(() => {
        navigate(`/error`, {
          state: setNavigateOptions(true, "/", invoiceErrorHeader, invoiceErrorMessage),
        });
      });
  };

  const handleCreateStatement = () => {
    navigate("/cnp");
  };

  return (
    <div className={styles.Index}>
      <div className={styles.Content}>
        {invoices.current.length <= 0 ? (
          <LandingView showLoader={showLoader} />
        ) : (
          <>
            <DesktopHistory
              showLoader={showLoader}
              invoices={invoices}
              firstInvoice={firstInvoice}
              lastInvoice={lastInvoice}
              getNextInvoices={getNextInvoices}
              getPreviousInvoices={getPreviousInvoices}
            />
            <MobileHistory
              showLoader={showLoader}
              invoices={invoices}
              firstInvoiceSK={firstInvoice}
              lastInvoiceSK={lastInvoice}
              getNextInvoices={getNextInvoices}
              getPreviousInvoices={getPreviousInvoices}
            />
          </>
        )}
        <div className={styles.Footer}>
          <div className={styles.Content}>
            <div onKeyDown={() => {}} tabIndex={0} role="button" className={styles.Button} onClick={handleCreateStatement}>
              Create iK Invoice
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default History;
